import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button, TextField, Box, Alert, CircularProgress, Typography } from "@mui/material";
import { createRequestPeriod, updateRequestPeriod } from "../services/userService";
import { RequestPeriod } from "../models/Company";

interface RequestPeriodData {
  id: number;
  periodName: string;
  startTime: string;
  endTime: string;  
}

interface CreateRequestPeriodDialogProps {
  companyId: number;
  companyName: string;  
  onSuccess?: (period?: RequestPeriod, isUpdate?: boolean) => void;
  requestPeriod?: RequestPeriodData; // If provided, we're in update mode
}

const CreateRequestPeriodDialog: React.FC<CreateRequestPeriodDialogProps> = ({ 
  companyId, 
  companyName, 
  onSuccess,
  requestPeriod 
}) => {
  const { getAccessToken, userId } = useAuth();
  const isUpdateMode = !!requestPeriod;
  
  const [periodName, setPeriodName] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Populate form fields when in update mode, clear when in create mode
  useEffect(() => {
    if (requestPeriod) {
      setPeriodName(requestPeriod.periodName);
      
      // Convert ISO strings to datetime-local format
      const formatDateTime = (isoString: string) => {
        const date = new Date(isoString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      setStartTime(formatDateTime(requestPeriod.startTime));
      setEndTime(formatDateTime(requestPeriod.endTime));
    } else {
      // Clear form when switching to create mode
      setPeriodName("");
      setStartTime("");
      setEndTime("");
    }
    // Reset success and error states when switching modes
    setSuccess(false);
    setError(null);
  }, [requestPeriod]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!periodName.trim()) {
      setError("Period name is required");
      return;
    }

    if (!startTime || !endTime) {
      setError("Both start and end times are required");
      return;
    }

    const start = new Date(startTime);
    const end = new Date(endTime);

    if (end <= start) {
      setError("End time must be after start time");
      return;
    }

    if (!userId) {
      setError("User ID not available");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const requestData = {
        adminUserId: parseInt(userId, 10),
        periodName: periodName.trim(),
        startTime: start.toISOString(),
        endTime: end.toISOString()
      };

      const result = isUpdateMode
        ? await updateRequestPeriod(requestPeriod!.id, requestData, getAccessToken)
        : await createRequestPeriod(companyId, requestData, getAccessToken);

      if (result.ok) {
        setSuccess(true);
        
        if (!isUpdateMode) {
          setPeriodName("");
          setStartTime("");
          setEndTime("");
        }
        
        // Call success callback with the returned period
        if (onSuccess) {
          setTimeout(() => {
            onSuccess(result.data, isUpdateMode);
          }, 1000);
        } else {
          setTimeout(() => setSuccess(false), 5000);
        }
      } else {
        setError(result.error || `Failed to ${isUpdateMode ? 'update' : 'create'} request period`);
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isUpdateMode 
          ? `Update the request period for <strong>${companyName}</strong>.`
          : `Create a new request period for <strong>${companyName}</strong>. Users will be able to submit ratings during this period.`
        }
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Request period {isUpdateMode ? 'updated' : 'created'} successfully!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Period Name"
          name="periodName"
          value={periodName}
          onChange={(e) => setPeriodName(e.target.value)}
          required
          margin="normal"
          disabled={loading}
          helperText="E.g., Q1 2026, January Review"
        />

        <TextField
          fullWidth
          label="Start Time"
          name="startTime"
          type="datetime-local"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
          required
          margin="normal"
          disabled={loading}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          fullWidth
          label="End Time"
          name="endTime"
          type="datetime-local"
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
          required
          margin="normal"
          disabled={loading}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !periodName.trim() || !startTime || !endTime}
            startIcon={loading && <CircularProgress size={20} />}
            fullWidth
          >
            {loading 
              ? (isUpdateMode ? "Updating..." : "Creating...") 
              : (isUpdateMode ? "Update Request Period" : "Create Request Period")
            }
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default CreateRequestPeriodDialog;
